import { hasAuthLogoutIntent, readAuthSessionCookies } from '@artic/ui/lib/auth-cookies';
import {
  createDualStorage,
  ensureDualStorageSync,
  reconcileSupabaseSessionTokens,
} from '@artic/ui/lib/supabase-cookie-storage';
import { createClient } from '@supabase/supabase-js';

import type { Database } from '~/types/database.types';

import { parseAuthErrorCode } from './authHashError';
import { clearPriorSessionOnAuthCallback } from './clearPriorSessionOnAuthCallback';
import { guardedFetch } from './supabaseRefreshGuard';

/**
 * No-op lock function for Supabase Auth. Matches auth-js's internal `lockNoOp`
 * fallback (used when `persistSession: false` or `navigator.locks` is
 * unavailable). Relies on auth-js's in-memory guards — `initializePromise`,
 * `refreshingDeferred`, and the `pendingInLock` / `lockAcquired` re-entry
 * queue — plus `BroadcastChannel` for cross-tab state sync.
 *
 * Why not the default navigator.locks? The hub calls `supabase.auth.getSession()`
 * from ~50 API helpers (for Authorization headers) and `fetchWithAuth` calls
 * it internally for every Postgrest query. Default navigator.locks wedged on
 * hub.artic.works when a crashed or background tab held the origin-scoped
 * lock past its 10s acquireTimeout, throwing `AbortError at locks.js:98` and
 * landing magic-link confirm clicks back on the login form. See commits
 * fddbd93e, 636d16e6, 43eef0d2 for earlier partial mitigations.
 *
 * Why not processLock? Every pending lock call has its own deadline timer
 * that starts at enqueue time, not at turn-to-execute time. With dozens of
 * parallel getSession() callers on app startup, the chain backs up and tail
 * entries fire `Lock acquisition timed out after 10000ms` warnings even
 * though the lock is functioning. lockNoOp avoids outer-level serialization
 * entirely; auth-js's own in-memory guards are sufficient for the hub's
 * single-client-per-tab setup.
 */
const lockNoOp = async <R>(
  _name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> => fn();

// ── Capture auth hash before anything can modify the URL ────────────
// Supabase's implicit-grant redirect lands on the page with
// #access_token=…&refresh_token=… in the URL hash. The Supabase client
// reads this inside an async navigator.locks callback, but React Router
// can replace the URL (and lose the hash) before that callback fires.
// Snapshot the hash synchronously at module load so we can restore it
// if needed.
const _initialHash = window.location.hash;

/**
 * If this page load is a Supabase implicit-grant error redirect (a spent or
 * expired magic-link token bounces back with `#...&error_code=otp_expired`),
 * return that error code so the login page can surface an expired-link
 * message instead of silently dead-ending the user. Reads the hash snapshot
 * captured above, before any router navigation can strip it.
 */
export function getInitialAuthErrorCode(): string | null {
  return parseAuthErrorCode(_initialHash);
}

const cookieDomain = import.meta.env.VITE_AUTH_COOKIE_DOMAIN || undefined;

// If this load is a Supabase implicit-grant callback (magic-link confirm
// lands here with #access_token=…&refresh_token=…), wipe stale per-user
// state from any previous session in this browser BEFORE the Supabase
// client initialises and BEFORE Recoil atoms hydrate from localStorage.
// Without this the prior user's `artic_partner` cookie / `current_partner`
// localStorage leak into the first authenticated request and crash the
// app on /current-user. See [[debug-hub-magiclink-blank-screen]].
const initialHashParams = new URLSearchParams(
  _initialHash.startsWith('#') ? _initialHash.slice(1) : _initialHash,
);
const initialAuthCallbackAccessToken = initialHashParams.get('access_token');
const initialAuthCallbackRefreshToken = initialHashParams.get('refresh_token');
const initialAuthCallback = clearPriorSessionOnAuthCallback(_initialHash, cookieDomain);

export function isInitialAuthCallback(accessToken: string, refreshToken: string): boolean {
  return (
    initialAuthCallback &&
    accessToken === initialAuthCallbackAccessToken &&
    refreshToken === initialAuthCallbackRefreshToken
  );
}

const dualStorage = createDualStorage({
  domain: cookieDomain,
});

// Derive the storage key Supabase uses internally
let storageKey = 'sb-localhost-auth-token';
if (import.meta.env.VITE_APP_SUPABASE_URL) {
  storageKey = `sb-${new URL(import.meta.env.VITE_APP_SUPABASE_URL).hostname.split('.')[0]}-auth-token`;
} else {
  console.debug('VITE_APP_SUPABASE_URL is not set — using fallback storage key');
}

// Cookie-only apps such as Neo rotate the shared token pair without touching
// auth-js's persisted session. Adopt that newer pair before createClient can
// attempt the stale, already-consumed refresh token.
const cookieSession = readAuthSessionCookies();
if (cookieSession && !hasAuthLogoutIntent()) {
  reconcileSupabaseSessionTokens(storageKey, dualStorage, cookieSession);
}

// Ensure both localStorage and cookies are in sync (handles all prior states)
ensureDualStorageSync(storageKey, dualStorage);

// If the URL had auth tokens in the hash but they've already been stripped
// (e.g. by a router navigation), restore them so the Supabase client can
// detect the implicit-grant callback during initialisation.
if (_initialHash && /access_token=/.test(_initialHash) && !window.location.hash) {
  window.location.hash = _initialHash;
}

// Kill-switch fetch wrapper lives in `./supabaseRefreshGuard` so its
// pure-function pieces (`shouldShortCircuitRefresh`, `syntheticAuthFailure`)
// can be unit-tested without spinning up a Supabase client. See that
// file for the full rationale (Sentry ARTIC-HUB-12X).
export const supabase = createClient<Database>(
  import.meta.env.VITE_APP_SUPABASE_URL,
  import.meta.env.VITE_APP_SUPABASE_ANON_KEY,
  {
    auth: {
      storage: dualStorage,
      storageKey,
      lock: lockNoOp,
    },
    global: {
      fetch: guardedFetch,
    },
  },
);

// Dev-only: Playwright e2e drives auth state via window.__supabase.
if (import.meta.env.DEV) {
  (window as unknown as { __supabase?: typeof supabase }).__supabase = supabase;
}
